/**
 * Continuous (looping) marquee for the announcement bar.
 *
 * Each `.announcement-bar__marquee` holds a track with a single authored item.
 * We clone that item until the track is comfortably wider than its container,
 * then translate the track by exactly one item width so the loop is seamless
 * regardless of how long or short the announcement text is.
 */
(() => {
  const DEFAULT_SPEED = 60; // px per second

  /** @param {HTMLElement} marquee */
  function layout(marquee) {
    const track = marquee.querySelector('.announcement-bar__marquee-track');
    if (!(track instanceof HTMLElement)) return;

    const original = track.querySelector('.announcement-bar__marquee-item:not([data-marquee-clone])');
    if (!(original instanceof HTMLElement)) return;

    for (const clone of track.querySelectorAll('[data-marquee-clone]')) clone.remove();

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const itemWidth = original.getBoundingClientRect().width;
    if (!itemWidth) return;

    const distance = itemWidth + gap;
    const containerWidth = marquee.getBoundingClientRect().width;
    const copies = Math.max(2, Math.ceil((containerWidth * 2) / distance) + 1);

    const fragment = document.createDocumentFragment();
    for (let index = 1; index < copies; index++) {
      const clone = /** @type {HTMLElement} */ (original.cloneNode(true));
      clone.setAttribute('data-marquee-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      for (const link of clone.querySelectorAll('a')) link.setAttribute('tabindex', '-1');
      fragment.appendChild(clone);
    }
    track.appendChild(fragment);

    const speed = parseFloat(marquee.dataset.speed || '') || DEFAULT_SPEED;
    track.style.setProperty('--marquee-distance', `${distance}px`);
    track.style.setProperty('--marquee-duration', `${distance / speed}s`);
    marquee.setAttribute('data-marquee-ready', '');
  }

  /** @param {ParentNode} [root] */
  function refresh(root = document) {
    for (const marquee of root.querySelectorAll('.announcement-bar__marquee')) {
      layout(/** @type {HTMLElement} */ (marquee));
    }
  }

  let frame;
  function scheduleRefresh() {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => refresh());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(), { once: true });
  } else {
    refresh();
  }

  document.fonts?.ready.then(() => refresh());
  window.addEventListener('resize', scheduleRefresh);
  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:block:select', () => scheduleRefresh());
})();
